import { fetchWrapper } from '~/utils/fetchWrapper'

// Types matching your FastAPI backend
export interface OperatingSystem {
  os: string
  arch: string
  name: string
}

export interface CreateRunnerRequest {
  name: string
  os: string
  arch: string
}

export interface Runner {
  id: number
  uuid: string
  org_id: number
  name: string
  active: boolean
  os: string
  arch: string
}

export interface InstallRunnerResponse {
  uuid: string
  cmd: string
  cmd_windows?: string // WSL-wrapped command for Windows
  os?: string // OS type for the runner
}

export interface WithAbortSignal {
  signal?: AbortSignal
}

export interface WithWorkspace extends WithAbortSignal {
  workspaceUuid: string
}

// API Functions matching your FastAPI endpoints
export const getOperatingSystems = async ({
  workspaceUuid,
  signal
}: WithWorkspace): Promise<OperatingSystem[]> => {
  const path = `/api/workspace/${workspaceUuid}/runner/runtime`
  const response = await fetchWrapper(path, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json'
    },
    signal
  })

  if (!response.ok) {
    const reason = await response.text()
    throw new Error(`Failed to get operating systems. Reason: ${reason}`)
  }

  const data = await response.json()
  return data as OperatingSystem[]
}

export const createRunner = async (
  workspaceUuid: string,
  runnerData: CreateRunnerRequest,
  { signal }: WithAbortSignal = {}
): Promise<{ id: number; uuid: string }> => {
  const path = `/api/workspace/${workspaceUuid}/runner/`
  const response = await fetchWrapper(path, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(runnerData),
    signal
  })

  if (!response.ok) {
    const reason = await response.text()
    throw new Error(`Failed to create runner. Reason: ${reason}`)
  }

  const data = await response.json()
  return data as { id: number; uuid: string }
}

export const getRunners = async ({ workspaceUuid, signal }: WithWorkspace): Promise<Runner[]> => {
  const path = `/api/workspace/${workspaceUuid}/runner/list`
  const response = await fetchWrapper(path, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json'
    },
    signal
  })

  if (!response.ok) {
    const reason = await response.text()
    throw new Error(`Failed to get runners. Reason: ${reason}`)
  }

  const data = await response.json()
  return data as Runner[]
}

export const getRunnerDetails = async (
  workspaceUuid: string,
  runnerId: string,
  { signal }: WithAbortSignal = {}
): Promise<Runner> => {
  const path = `/api/workspace/${workspaceUuid}/runner/${runnerId}`
  const response = await fetchWrapper(path, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json'
    },
    signal
  })

  if (!response.ok) {
    const reason = await response.text()
    throw new Error(`Failed to get runner details. Reason: ${reason}`)
  }

  const data = await response.json()
  return data as Runner
}

export const postRunnerInstallSetup = async (
  workspaceUuid: string,
  runnerId: number,
  { signal }: WithAbortSignal = {}
): Promise<InstallRunnerResponse> => {
  const path = `/api/workspace/${workspaceUuid}/runner/${runnerId}/install/setup`
  const response = await fetchWrapper(path, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    signal
  })

  if (!response.ok) {
    const reason = await response.text()
    throw new Error(`Failed to get runner install command. Reason: ${reason}`)
  }

  const data = await response.json()
  return data as InstallRunnerResponse
}

export const updateRunner = async (
  workspaceUuid: string,
  runnerId: number,
  runnerData: Partial<CreateRunnerRequest>,
  { signal }: WithAbortSignal = {}
): Promise<Runner> => {
  const path = `/api/workspace/${workspaceUuid}/runner/${runnerId}`
  const response = await fetchWrapper(path, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(runnerData),
    signal
  })

  if (!response.ok) {
    const reason = await response.text()
    throw new Error(`Failed to update runner. Reason: ${reason}`)
  }

  const data = await response.json()
  return data as Runner
}

export const deleteRunner = async (
  workspaceUuid: string,
  runnerId: number,
  { signal }: WithAbortSignal = {}
): Promise<void> => {
  const path = `/api/workspace/${workspaceUuid}/runner/${runnerId}`
  const response = await fetchWrapper(path, {
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json'
    },
    signal
  })

  if (!response.ok) {
    const reason = await response.text()
    throw new Error(`Failed to delete runner. Reason: ${reason}`)
  }
}
